import {apiRequest} from '@/api/client';
import {
    cancelScheduledDowngrade,
    getBillingInfo,
    getInvoice,
    getInvoices,
    getPricing,
    getProration,
    getSiteUsage,
    getSubscription,
    getUsage,
    initiateUpgrade,
    scheduleDowngrade,
    validateDowngrade
} from '@/api/billing';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

export function useBillingInfo() {
    return useQuery({
        queryKey: ['billing-info'],
        queryFn: getBillingInfo
    });
}

export function useSubscription(siteId: string | undefined) {
    return useQuery({
        queryKey: ['subscription', siteId],
        queryFn: () => getSubscription(siteId!),
        enabled: !!siteId
    });
}

export function useUsage() {
    return useQuery({
        queryKey: ['billing-usage'],
        queryFn: getUsage
    });
}

export function useSiteUsage(siteId: string | undefined) {
    return useQuery({
        queryKey: ['site-usage', siteId],
        queryFn: () => getSiteUsage(siteId!),
        enabled: !!siteId
    });
}

export function useInitiateUpgrade() {
    return useMutation({
        mutationFn: ({planId, billingPeriod = 'monthly'}: { planId: string; billingPeriod?: 'monthly' | 'yearly' | 'lifetime' }) => initiateUpgrade(planId, billingPeriod)
    });
}

export function useValidateDowngrade() {
    return useMutation({
        mutationFn: ({planId}: { planId: string }) => validateDowngrade(planId)
    });
}

export function useScheduleDowngrade() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({planId}: { planId: string }) => scheduleDowngrade(planId),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['billing-info']});
            queryClient.invalidateQueries({queryKey: ['billing-usage']});
        }
    });
}

export function useCancelScheduledDowngrade() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => cancelScheduledDowngrade(),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['billing-info']});
            queryClient.invalidateQueries({queryKey: ['billing-usage']});
        }
    });
}

export function useInvoices(limit: number = 20, offset: number = 0, siteId?: string) {
    return useQuery({
        queryKey: ['invoices', {limit, offset, siteId}],
        queryFn: () => getInvoices(limit, offset, siteId)
    });
}

export function useSiteInvoices(siteId: string | undefined, limit: number = 10) {
    return useQuery({
        queryKey: ['site-invoices', siteId, limit],
        queryFn: () => getInvoices(limit, 0, siteId),
        enabled: !!siteId
    });
}

export function useInvoice(invoiceId: string | undefined) {
    return useQuery({
        queryKey: ['invoice', invoiceId],
        queryFn: () => getInvoice(invoiceId!),
        enabled: !!invoiceId
    });
}

export function usePricing() {
    return useQuery({
        queryKey: ['pricing'],
        queryFn: getPricing,
        staleTime: 5 * 60 * 1000 // 5 minutes
    });
}

export function useProration(
    siteId: string | undefined,
    planId: string | undefined,
    billingPeriod: string = 'monthly'
) {
    return useQuery({
        queryKey: ['proration', siteId, planId, billingPeriod],
        queryFn: () => getProration(siteId!, planId!, billingPeriod),
        enabled: !!siteId && !!planId
    });
}

export function useAccountOverrides() {
    return useQuery({queryKey: ['account-overrides'], queryFn: () => apiRequest<{key: string; value: number; expires_at: string | null}[]>('/billing/overrides')});
}
